import { useContext, useState } from "react";

import { Forum, ContentPasteSearch, ArrowDropDown } from "@mui/icons-material";
import {
  Badge,
  Drawer as MuiDrawer,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import classNames from "classnames";
import { useLocation, useNavigate } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import {
  APPBAR_HEIGHT,
  DRAWER_BREAKPOINT,
  DRAWER_WIDTH_COLLAPSED,
  DRAWER_WIDTH_EXPANDED,
} from "../constants";
import { handleSwitchCompany, UserContext } from "../helpers";
import { drawerCollapse, drawerExpansion } from "../theme";
import DrawerItem from "./DrawerItem";
import { getCompanyUnreadCounts } from "./messaging/helpers/unreadCounts";
import { useBadgePulse } from "./messaging/hooks/useBadgePulse";
import { badgePulseStyles } from "./messaging/styles/badgePulseStyles";

import { CompanyType } from "types";

const useStyles = makeStyles()((theme) => ({
  appBarMargin: {
    marginTop: APPBAR_HEIGHT,
  },
  badge: {
    backgroundColor: theme.palette.error.dark,
    borderRadius: "4px",
    color: "white",
    fontSize: theme.typography.pxToRem(10),
    fontWeight: 700,
    lineHeight: 1,
    padding: "2px 5px",
    position: "absolute",
    top: "2px",
    left: "2px",
    right: "auto",
    width: "auto",
    height: "auto",
  },
  company: {
    backgroundColor: "rgba(255, 255, 255, 0.85)",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    fontSize: theme.typography.pxToRem(16),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  companyText: {
    color: theme.typography.h1.color,
    fontWeight: 700,
    textAlign: "center",
  },
  companyTextDropdown: {
    "& svg": {
      top: theme.spacing(0.75),
      position: "relative",
    },
  },
  drawerCollapse: {
    transition: drawerCollapse.transitionWidth,
    width: DRAWER_WIDTH_COLLAPSED,
  },
  drawerExpand: {
    transition: drawerExpansion.transitionWidth,
    width: DRAWER_WIDTH_EXPANDED,
  },
  drawerMobile: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  drawerPaper: {
    border: "none",
    boxShadow: theme.shadows[2],
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  drawerWidth: {
    width: 200,
    display: "inherit",
    flexShrink: 1,
    background: theme.palette.background.grey,
  },
  image: {
    width: 20,
    height: 20,
  },
  list: {
    borderTop: "1px solid rgba(255, 255, 255, 0.1)",
  },
  logoContainer: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.up(DRAWER_BREAKPOINT)]: {
      cursor: "pointer", // onClick is set only on the large screen
    },
    marginLeft: 24,
    paddingRight: "35px",
    borderRight: "1px solid #797979",
  },
  logo: {
    width: 122,
  },
  mCMessageBadge: {
    top: -6,
    left: -6,
  },
  ...badgePulseStyles,
  companyBadge: {
    backgroundColor: theme.palette.error.dark,
    color: "white",
  },
  mCMessageBadgeCompany: {
    marginLeft: 20,
  },
  subCompanyList: {
    display: "none",
    backgroundColor: "rgba(255, 255, 255, 0.65)",
    "&:hover": {
      backgroundColor: "#F2F2F2",
    },
  },
  subCompanyListOpen: {
    display: "block",
  },
  subCompanyListItem: {
    textAlign: "center",
    color: theme.palette.text.primary,
    left: -4,
  },
  subList: {
    padding: 0,
  },
  trial: {
    padding: "15px 30px",
    backgroundColor: "rgba(255, 255, 255, 1)",
  },
}));

interface ComponentPropsType {
  badgeCounts: Record<number | string, number>;
  isBrowserDrawerExpanded: boolean;
  isMobileDrawerOpen: boolean;
  onClose: Function;
}

const Drawer = (props: ComponentPropsType) => {
  const theme = useTheme();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up(DRAWER_BREAKPOINT));
  const { classes, cx } = useStyles();
  const { badgeCounts, isBrowserDrawerExpanded, isMobileDrawerOpen, onClose } =
    props;
  const [showCompanyDropdown, setShowCompanyDropdown] =
    useState<boolean>(false);
  const { currentCompanyUnreadCount, otherCompaniesUnreadCount } =
    getCompanyUnreadCounts(badgeCounts, user.company.id);
  const pulseCurrentCompany = useBadgePulse(currentCompanyUnreadCount);
  const pulseOtherCompanies = useBadgePulse(otherCompaniesUnreadCount);

  const closeMobileDrawer =
    isMobileDrawerOpen && !isLargeScreen ? onClose : () => {};

  const handleShowCompanyDropdown = () => {
    setShowCompanyDropdown(!showCompanyDropdown);
  };

  const onSwitchCompany = (companyId: number) => {
    setShowCompanyDropdown(false);
    handleSwitchCompany(companyId, navigate);
  };

  return (
    <MuiDrawer
      classes={{
        paper: classNames(classes.drawerPaper, {
          [classes.drawerWidth]: isLargeScreen,
          [classes.drawerExpand]: isBrowserDrawerExpanded && isLargeScreen,
          [classes.drawerCollapse]: !isBrowserDrawerExpanded && isLargeScreen,
        }),
      }}
      open={isMobileDrawerOpen}
      onClose={() => onClose()}
      variant={isLargeScreen ? "permanent" : "persistent"}
    >
      {!isLargeScreen && (
        <List
          className={classNames(
            classes.list,
            classes.appBarMargin,
            classes.drawerMobile
          )}
        >
          <ListItem
            onClick={handleShowCompanyDropdown}
            classes={{ root: classes.company }}
          >
            <ListItemText
              primary={
                <>
                  {user.companies.length > 1 &&
                    otherCompaniesUnreadCount > 0 && (
                      <>
                        <Badge
                          badgeContent={otherCompaniesUnreadCount}
                          max={99}
                          classes={{
                            badge: cx(
                              classes.companyBadge,
                              pulseOtherCompanies && classes.badgePulse
                            ),
                          }}
                          className={classes.mCMessageBadge}
                          data-testid="app-bar-multiple-company-messages-badge"
                          title={`${otherCompaniesUnreadCount} unread in other companies`}
                          aria-label={`${otherCompaniesUnreadCount} unread in other companies`}
                        ></Badge>
                      </>
                    )}
                  {user.company.name}
                  {user.companies.length > 1 && <ArrowDropDown />}
                </>
              }
              disableTypography={true}
              className={classNames(classes.companyText, {
                [classes.companyTextDropdown]: user.companies.length > 1,
              })}
            />
          </ListItem>
          {user.companies.length > 1 && (
            <List
              component="div"
              disablePadding
              className={classNames(classes.subList, classes.subCompanyList, {
                [classes.subCompanyListOpen]: showCompanyDropdown,
              })}
            >
              {user.companies
                .filter(
                  (company: CompanyType) => company.id !== user.company.id
                )
                .map((oneCompany: CompanyType) => {
                  return (
                    <ListItem
                      id={oneCompany.id.toString()}
                      button
                      className={classes.subCompanyListItem}
                      key={oneCompany.id}
                      onClick={() => onSwitchCompany(oneCompany.id)}
                    >
                      <ListItemText
                        primary={
                          <>
                            {oneCompany.name}
                            <Badge
                              badgeContent={badgeCounts[oneCompany.id] || 0}
                              max={99}
                              classes={{ badge: classes.companyBadge }}
                              className={classes.mCMessageBadgeCompany}
                              title={`${
                                badgeCounts[oneCompany.id] || 0
                              } unread in ${oneCompany.name}`}
                              aria-label={`${
                                badgeCounts[oneCompany.id] || 0
                              } unread in ${oneCompany.name}`}
                            />
                          </>
                        }
                      />
                    </ListItem>
                  );
                })}
            </List>
          )}
        </List>
      )}
      <List
        className={classNames(classes.list, {
          [classes.appBarMargin]: isLargeScreen,
        })}
      >
        <DrawerItem
          id="messages"
          icon={
            <Badge
              badgeContent={currentCompanyUnreadCount}
              max={99}
              classes={{
                badge: cx(
                  classes.badge,
                  pulseCurrentCompany && classes.badgePulse
                ),
              }}
            >
              <Forum />
            </Badge>
          }
          isDrawerOpen={isBrowserDrawerExpanded || isMobileDrawerOpen}
          text="Messages"
          data-testid="messages"
          to="/messaging"
          isSelected={Boolean(
            location.pathname.match("/messaging") || location.pathname === "/"
          )}
          onClick={closeMobileDrawer}
        />
        <DrawerItem
          id="inspections"
          icon={<ContentPasteSearch />}
          isDrawerOpen={isBrowserDrawerExpanded || isMobileDrawerOpen}
          text="Inspections"
          data-testid="inspections"
          to="/inspections"
          isSelected={Boolean(location.pathname.match("/inspections"))}
          onClick={closeMobileDrawer}
        />
      </List>
    </MuiDrawer>
  );
};

export default Drawer;
